import requests from "./httpServices.js";

class FolderService {
  /**
   * Create a new folder
   * @param data - Folder creation data
   * @returns Promise<FolderResponse>
   */
  async createFolder(data) {
    try {
      const response = await requests.post(`/element/create-folder`, data);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get all folders with optional pagination and filters
   * @param params - Query parameters for filtering and pagination
   * @returns Promise<FoldersResponse>
   */
  async getFolders() {
    try {
      const url = `/element/get-folders`;
      const response = await requests.get(url);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get a single folder by ID
   * @param folderId - Folder ID
   * @returns Promise<FolderResponse>
   */
  async getFolderById(folderId) {
    try {
      const response = await requests.get(`/element/get-folders/${folderId}`);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getCtaByFolderId(folderId) {
    try {
      const response = await requests.get(`/element/cta/${folderId}`);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update a folder
   * @param folderId - Folder ID
   * @param data - Folder update data
   * @returns Promise<FolderResponse>
   */
  async updateFolder(folderId, data) {
    try {
      const response = await requests.put(`/element/folder/${folderId}`, data);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete a folder
   * @param folderId - Folder ID
   * @returns Promise<DeleteFolderResponse>
   */
  async deleteFolder(folderId) {
    try {
      const response = await requests.delete(
        `/element/delete-folder/${folderId}`
      );
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get root folders (folders without parent)
   * @returns Promise<FoldersResponse>
   */
  async getRootFolders() {
    try {
      const response = await requests.get(`/element/folders?parentId=null`);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getPresetsByRoomId(roomId) {
    try {
      const response = await requests.get(
        `/element/presets-by-room-id/${roomId}`
      );
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param error - Error object
   * @returns Error with formatted message
   */
  handleError(error) {
    if (error.response?.data) {
      const { message, errors } = error.response.data;

      if (errors && typeof errors === "object") {
        // Handle validation errors
        const errorMessages = Object.values(errors).flat();
        return new Error(errorMessages.join(", "));
      }

      if (message) {
        return new Error(message);
      }
    }

    if (error.message) {
      return new Error(error.message);
    }

    return new Error("An unexpected error occurred");
  }
}

// Export singleton instance
const folderService = new FolderService();
export default folderService;
